import React, { ReactNode } from 'react';
import { Box } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { classOptions, equipmentConditions, equipmentYears, makeOptions } from 'modules/equipment';
import { FormSectionHeader, equipmentMileageFormatter } from 'modules/desking';
import { AutocompleteValueField, CurrencyInputField, InputField, SelectField } from 'components/form';
import {
  DeskingConfigurationBaseOnAccountFormFieldsRequire,
  FinanceProgramFormValues,
  FormSectionProps,
} from 'modules/desking/types';
import { StartDealRenderProps } from '../types';
import { Nullable, SelectOption } from 'global';
import { SerialNumberVinInput } from 'modules/desking/ui/components';
import { useNotifications } from 'modules/notification';
import { getTrnsactClassValueFromVinDecoding, getTrnsactMakeValueFromVinDecoding } from 'modules/desking/lib';

type Props = FormSectionProps &
  StartDealRenderProps & {
    dynamicFieldsSlot: ReactNode;
    onChangeAllProductsConfig: (configField: string, nextValue: string) => void;
    formFieldsRequire: Nullable<DeskingConfigurationBaseOnAccountFormFieldsRequire>;
  };

export const EquipmentSection = ({
  sectionName,
  dynamicFieldsSlot,
  formFieldsRequire,
  onChangeAllProductsConfig,
}: Props) => {
  const { control, setValue } = useFormContext<FinanceProgramFormValues>();

  const { showNotification } = useNotifications();

  const onSearchByVinResponse = (vinDecodingResult: any) => {
    const equipmentInfo = vinDecodingResult?.Results?.[0];

    if (equipmentInfo?.ErrorCode !== '0') {
      showNotification('Could not decode VIN', { type: 'info' });
      return;
    }

    setValue('equipment.model', equipmentInfo.Model);
    setValue('equipment.year', equipmentInfo.ModelYear);
    setValue('equipment.make', getTrnsactMakeValueFromVinDecoding(equipmentInfo.Make));
    setValue('equipment.class', getTrnsactClassValueFromVinDecoding(equipmentInfo.GVWR));
    onChangeAllProductsConfig('EQUIPMENT_CLASS', getTrnsactClassValueFromVinDecoding(equipmentInfo.GVWR));
  };

  const [mileage, serialNumberVin] = useWatch({ control, name: ['equipment.mileage', 'equipment.serialNumberVin'] });

  return (
    <Box className="section">
      <FormSectionHeader name="Equipment" sectionName={sectionName} />

      <Box className="sectionFields">
        <Box className="row">
          <InputField label="Quantity" control={control} name="equipment.quantity" />

          <AutocompleteValueField<SelectOption, false, FinanceProgramFormValues>
            control={control}
            name="equipment.year"
            options={equipmentYears}
            textFieldProps={{ label: 'Year', size: 'small' }}
          />

          <SelectField label="Make" control={control} name="equipment.make" options={makeOptions} />

          <InputField label="Model" control={control} name="equipment.model" />

          <SelectField
            label="Class"
            control={control}
            name="equipment.class"
            options={classOptions}
            extraAction={nextValue => {
              onChangeAllProductsConfig('EQUIPMENT_CLASS', nextValue.toString());
            }}
          />

          <SerialNumberVinInput
            onResponse={onSearchByVinResponse}
            inputFieldProps={{
              inputProps: {
                required: formFieldsRequire?.['equipment.serialNumberVin']?.value,
                helperText: formFieldsRequire?.['equipment.serialNumberVin']?.message,
                error: formFieldsRequire?.['equipment.serialNumberVin']?.value && !serialNumberVin,
              },
            }}
          />
        </Box>

        <Box className="row">
          <InputField label="Stock Id" control={control} name="equipment.stockId" />

          <SelectField label="Condition" control={control} name="equipment.newUsed" options={equipmentConditions} />

          <InputField
            label="Mileage"
            control={control}
            name="equipment.mileage"
            formatter={value => equipmentMileageFormatter(value, 'toUpdate')}
            inputProps={{
              required: formFieldsRequire?.['equipment.mileage']?.value,
              helperText: formFieldsRequire?.['equipment.mileage']?.message,
              error: formFieldsRequire?.['equipment.mileage']?.value && !mileage,
            }}
          />

          <InputField control={control} label="Description" name="equipment.description" />

          <CurrencyInputField control={control} label="Wholesale Cost" name="equipment.totalWholesaleCost" />

          <CurrencyInputField label="Retail Price" control={control} name="equipment.retailPrice" />
        </Box>

        {dynamicFieldsSlot}
      </Box>
    </Box>
  );
};
